import _ from 'lodash';
import { UTM_PARAMS_EXCLUDED_HOSTNAMES, UTM_MEDIUM, HAND_OFF_PATH } from "config";
import { getHostname } from "lib/configUtils";
import { durationToISO8601, getExternalQueryParams } from "lib/utils";
import { TimeTypes } from 'models/DateTime';

const DATETIME_FORMAT_ISO = "yyyy-MM-dd'T'HH:mm";
const DATE_FORMAT = "yyyy-MM-dd";

const getRailcardsURL = (railcards) => {
  if (!railcards || !railcards.length) {
    return "";
  }

  // Example: 2TRx1,DICx2
  return railcards
    .map((railcard) => `${railcard.railcardCode}x${railcard.quantity}`)
    .join(",");
};

const getStationParam = (station) => (station.crs ? station.crs : station.nlc);

const buildQueryString = (params = []) =>
  params
    .filter(Boolean)
    .reduce(
      (acc, param) =>
        (acc ? `${acc}&` : "?") +
        `${param.name}=${encodeURIComponent(param.value)}`,
      ""
    );

const buildDateTimeString = dateTime => {
  const prefix = dateTime.timeType === TimeTypes.ARRIVING_AT ? 'Arrive_at_' : '';
  const dateTimeString = dateTime.format(DATETIME_FORMAT_ISO);

  return `${prefix}${dateTimeString}`;
};

export const getUTMParams = ({
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
  mode,
} = {}) => {
  const hostname = getHostname(mode);

  const isExcluded = UTM_PARAMS_EXCLUDED_HOSTNAMES.some((excluded) =>
    excluded.test(hostname)
  );

  return isExcluded
    ? []
    : [
        { name: "utm_campaign", value: utmCampaign || hostname },
        { name: "utm_content", value: utmContent || hostname },
        { name: "utm_medium", value: utmMedium || `${UTM_MEDIUM}-${mode}` },
        { name: "utm_source", value: utmSource || hostname },
      ].filter((param) => param.value);
};

export const getSearchURL = ({
  data,
  redirectUrl,
  mode,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
}) => {
  const basePath = "/journeys/";

  let viaAvoidParam = null;
  let promoParam = null;

  if (data.via) {
    if (data.viaAvoidType === "via" || data.viaAvoidType === "avoid") {
      const viaAvoidType = data.viaAvoidType;
      const viaAvoidStation = data.viaAvoid.nlc;

      viaAvoidParam = { name: viaAvoidType, value: viaAvoidStation };
    }
  }

  if (data.promoCode) {
    promoParam = { name: 'promoCode', value: data.promoCode.promoCode };
  }

  const queryParams = [
    viaAvoidParam,
    promoParam,
    { name: "departNow", value: data.fromTimeType === "depart" ? "yes" : "no" },
    { name: "realTime", value: "no" },
    { name: "searchPreferences", value: "" },
    { name: "showAdditionalRoutes", value: "no" },
    { name: "showCheapest", value: "no" },
    { name: "tocSpecific", value: "no" },
    ...getExternalQueryParams(),
    ...getUTMParams({
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
    }),
  ];

  const leavingDateTime = buildDateTimeString(data.fromDateTime);
  const arrivingDateTime =
    data.ticketType === "return"
      ? buildDateTimeString(data.toDateTime)
      : data.ticketType === "open"
      ? "open"
      : "";
  const railcardsURL = getRailcardsURL(data.railcards);
  const adultsURL = data.adults || "";
  const childrenURL = data.children || "";
  const queryParamsURL = buildQueryString(queryParams);

  const urlSearch = `${redirectUrl}${basePath}${getStationParam(
    data.stationFrom
  )}/${getStationParam(
    data.stationTo
  )}/${leavingDateTime}/${arrivingDateTime}/${adultsURL}/${childrenURL}/${railcardsURL}${queryParamsURL}`;

  return urlSearch;
};

const makeRailcardURL = (data) => {
  let railcardURL = "";

  if (data.hasRailcard && !data.hasJCPRailcard) {
    railcardURL = "/TSU";
  } else if (!data.hasRailcard && data.hasJCPRailcard) {
    railcardURL = "/JCP";
  } else if (data.hasRailcard && data.hasJCPRailcard) {
    railcardURL = "/TSU,JCP";
  }

  return railcardURL;
}

export const getSeasonSearchURL = ({
  data,
  redirectUrl,
  mode,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
  searchFormsController
}) => {
  const basePath = "/season-tickets/";

  let startingDate = data.fromDateTime.format(DATE_FORMAT);
  let promoParam = null;

  const urlTypeMapping = {
    isFlexi: "flexi",
    isWeekly: "weekly",
    isMonthly: "monthly",
    isAnnual: "annual",
  };

  const ticketTypes = ["isFlexi", "isWeekly", "isMonthly", "isAnnual", "isCustomLength"]
    .filter((type) => data[type])
    .map((type) => urlTypeMapping[type]);

  if (data.isCustomLength) {
    const customTicketType = durationToISO8601({
      start: data.fromDateTime.toDate(),
      end: data.toDateTime.toDate(),
      includeLastDay: true,
    });
    ticketTypes.push(customTicketType);
  }

  if (data.promoCode) {
    promoParam = { name: 'promoCode', value: data.promoCode.promoCode };
  }

  const ticketTypesURL = ticketTypes.join(",");

  const isRailcard1617Checked = searchFormsController.season.getValues('hasRailcard');

  const queryParams = [
    { name: "searchPreferences", value: "" },
    { name: "showAdditionalRoutes", value: "no" },
    { name: "isRailcard1617Checked", value: isRailcard1617Checked },
    promoParam,
    ...getExternalQueryParams(),
    ...getUTMParams({
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
    }),
  ];
  const queryParamsURL = buildQueryString(queryParams);

  const passengerTypeURL = `/${data.passengerType}`;

  const railcardURL = makeRailcardURL(data)

  const urlSearch = `${redirectUrl}${basePath}${getStationParam(
    data.stationFrom
  )}/${getStationParam(
    data.stationTo
  )}/${startingDate}/${ticketTypesURL}${passengerTypeURL}${railcardURL}${queryParamsURL}`;

  return urlSearch;
};

export const getSmartCarnetSearchURL = ({
  data,
  redirectUrl,
  mode,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
}) => {
  const basePath = "/smart-carnet/";

  const queryParams = [
    ...getUTMParams({
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
    })
  ];
  const queryParamsURL = buildQueryString(queryParams);

  let startingDate = data.fromDateTime.format(DATE_FORMAT);
  const urlSearch = `${redirectUrl}${basePath}${getStationParam(
    data.stationFrom
  )}/${getStationParam(data.stationTo)}/${startingDate}`;

  return urlSearch + queryParamsURL;
};

export const getRoversSearchURL = ({
  data,
  redirectUrl,
  mode,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
}) => {
  const basePath = "/roversrangers/";

  let startingDate = data.fromDateTime.format(DATE_FORMAT);

  const adultsURL = data.adults || "";
  const childrenURL = data.children || "";
  const railcardsURL = getRailcardsURL(data.railcards);

  const urlSearch = `${redirectUrl}${basePath}${startingDate}/${data.roverType}/${adultsURL}/${childrenURL}/${railcardsURL}`;
  const queryParamsURL = buildQueryString([
    ...getExternalQueryParams(),
    ...getUTMParams({
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
    }),
  ]);

  return urlSearch + queryParamsURL;
};

export const getDaySaveSearchURL = ({
  data,
  redirectUrl,
  mode,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
}) => {
  const basePath = "/daysave/";

  let startingDate = data.fromDateTime.format(DATE_FORMAT);

  const adultsURL = data.adults || "";
  const childrenURL = data.children || "";

  const urlSearch = `${redirectUrl}${basePath}${startingDate}/${getStationParam(
    data.stationFrom
  )}/${adultsURL}/${childrenURL}`;
  const queryParamsURL = buildQueryString([
    ...getExternalQueryParams(),
    ...getUTMParams({
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
    }),
  ]);

  return urlSearch + queryParamsURL;
};

export const getFavouritesSearchURL = ({
  data,
  useDeliveryPreference,
  redirectUrl,
  mode,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
}) => {
  const handOffData = {
    ...data,
    startDate: data.startDate.format(DATE_FORMAT),
    useDeliveryPreference
  };
  const handOffParams = _.keys(handOffData).map(key => ({ name: key, value: handOffData[key] }));

  const urlSearch = `${redirectUrl}${HAND_OFF_PATH}`;
  const queryParamsURL = buildQueryString([
    ...handOffParams,
    ...getExternalQueryParams(),
    ...getUTMParams({
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
    }),
  ]);

  return urlSearch + queryParamsURL;
};
